import Keycloak from 'keycloak-js';
import * as React from 'react';

const BACKEND_URL = `${process.env.REACT_APP_BACKEND_URL || ''}/externalProfile/${process.env.REACT_APP_MODULE}`;

export let keycloak: Keycloak;

interface KeycloakContextValue {
  keycloak: Keycloak;
  initialized: boolean;
}

export const KeycloakContext = React.createContext<KeycloakContextValue>({} as KeycloakContextValue);

export function useKeycloak(): KeycloakContextValue {
  return React.useContext(KeycloakContext);
}

let initPromise: Promise<void> | null = null;

export async function initialize(clientId: string): Promise<void> {
  if (initPromise) {
    return initPromise;
  }

  const urlParams = new URLSearchParams(window.location.search);
  console.log('[KC init] URL params on load:', {
    code: urlParams.has('code') ? 'present' : 'absent',
    state: urlParams.get('state'),
    iss: urlParams.get('iss'),
    error: urlParams.get('error'),
    error_description: urlParams.get('error_description'),
    fullSearch: window.location.search.substring(0, 200),
    fullHash: window.location.hash.substring(0, 200),
  });

  initPromise = fetch(`${BACKEND_URL}/keycloak.json?_=${Date.now()}`, { cache: 'no-store' })
    .then(res => {
      console.log('[KC init] keycloak.json status:', res.status);
      if (!res.ok) {
        throw new Error(`keycloak.json ${res.status}`);
      }
      return res.json();
    })
    .then((config: { url: string; realm: string }) => {
      console.log('[KC init] config:', { url: config.url, realm: config.realm, clientId });
      keycloak = new Keycloak({ ...config, clientId });

      // KC 26 appends `iss` to the callback URL per RFC 9207, but keycloak-js 21
      // doesn't recognise it as an auth param. When it reconstructs the redirect_uri
      // for the token exchange it leaves `iss` in the URL, causing a redirect_uri
      // mismatch → KC returns invalid_grant → keycloak-js calls login() again →
      // another `iss` accumulates → infinite loop.
      // Strip `iss` before init() reads window.location so the reconstructed
      // redirect_uri is clean and matches the original authorization request.
      {
        const params = new URLSearchParams(window.location.search);
        if (params.has('iss')) {
          params.delete('iss');
          const clean = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
          console.log('[KC init] stripping iss from URL, clean URL:', clean);
          window.history.replaceState(null, '', clean);
        }
      }

      // Attach event handlers BEFORE init() so we capture the exact exchange result
      keycloak.onReady = (authenticated) =>
        console.log('[KC onReady] authenticated:', authenticated);
      keycloak.onAuthSuccess = () =>
        console.log('[KC onAuthSuccess] token:', !!keycloak.token, 'iss:', keycloak.tokenParsed?.iss);
      keycloak.onAuthError = (errorData) =>
        console.error('[KC onAuthError] error:', errorData.error, 'description:', errorData.error_description);
      keycloak.onAuthRefreshError = () =>
        console.error('[KC onAuthRefreshError]');
      keycloak.onTokenExpired = () =>
        console.log('[KC onTokenExpired]');

      console.log('[KC init] calling keycloak.init()');
      return keycloak.init({
        onLoad: 'login-required',
        checkLoginIframe: false,
        responseMode: 'query',
        pkceMethod: 'S256',
        // keycloak-js 21 checks nonces in access + refresh tokens, but Keycloak 26
        // follows the OIDC spec and only includes the nonce in the ID token.
        // undefined != storedNonce → TRUE → clearToken() → infinite login loop.
        // PKCE (S256) already provides equivalent replay-attack protection.
        useNonce: false,
      });
    })
    .then((authenticated: boolean) => {
      console.log('[KC init] init() resolved, authenticated:', authenticated, 'token:', !!keycloak.token);
      if (keycloak.tokenParsed) {
        console.log('[KC init] token iss:', keycloak.tokenParsed.iss, 'azp:', keycloak.tokenParsed.azp);
      }
      if (!authenticated) {
        throw new Error('[KC init] not authenticated after init');
      }
    })
    .catch(err => {
      console.error('[KC init] FAILED:', err);
      initPromise = null;  // allow retry on next hard-reload, but do NOT call login() here
      throw err;
    });
  return initPromise;
}

export function initializeMock() {
  keycloak = new Keycloak();
}